import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Users, ChevronRight, Search, Filter, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const CareersPage = () => {
  const [filters, setFilters] = useState({
    department: 'All',
    location: 'All',
    experience: 'All',
    type: 'All',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 6;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const departments = ['All', 'Engineering', 'Consulting', 'Sales', 'Data Science', 'Cybersecurity', 'Cloud', 'HR', 'Management'];
  const locations = ['All', 'Malaysia', 'Singapore', 'UAE', 'UK', 'USA', 'India', 'Canada', 'Australia', 'Germany', 'Japan'];
  const experienceLevels = ['All', 'Entry Level (0-2 years)', 'Mid Level (3-5 years)', 'Senior Level (5+ years)', 'Executive Level (10+ years)'];
  const jobTypes = ['All', 'Full-time', 'Part-time', 'Contract', 'Remote', 'Hybrid'];

  const jobs = [
    {
      id: 1,
      title: 'Senior AI/ML Engineer',
      department: 'Engineering',
      location: 'Kuala Lumpur, Malaysia',
      country: 'Malaysia',
      city: 'Kuala Lumpur',
      type: 'Full-time',
      experience: 'Senior Level (5+ years)',
      experienceYears: '5+ years',
      description: 'Lead AI/ML initiatives and develop cutting-edge solutions for enterprise clients.',
      requirements: ['Python, TensorFlow, PyTorch', 'Cloud platforms (AWS, Azure)', 'MLOps experience'],
      posted: '2 days ago',
      salary: '$80,000 - $120,000'
    },
    {
      id: 2,
      title: 'Cloud Solutions Architect',
      department: 'Consulting',
      location: 'Singapore',
      country: 'Singapore',
      city: 'Singapore',
      type: 'Full-time',
      experience: 'Senior Level (5+ years)',
      experienceYears: '7+ years',
      description: 'Design and implement cloud transformation strategies for Fortune 500 companies.',
      requirements: ['AWS/Azure certifications', 'Enterprise architecture', 'Client-facing experience'],
      posted: '1 week ago',
      salary: '$90,000 - $140,000'
    },
    {
      id: 3,
      title: 'Cybersecurity Consultant',
      department: 'Cybersecurity',
      location: 'Dubai, UAE',
      country: 'UAE',
      city: 'Dubai',
      type: 'Full-time',
      experience: 'Mid Level (3-5 years)',
      experienceYears: '4+ years',
      description: 'Provide cybersecurity consulting and implement zero-trust architectures.',
      requirements: ['CISSP/CISM certification', 'Penetration testing', 'Risk assessment'],
      posted: '3 days ago',
      salary: '$70,000 - $100,000'
    },
    {
      id: 4,
      title: 'Data Scientist',
      department: 'Data Science',
      location: 'London, UK',
      country: 'UK',
      city: 'London',
      type: 'Full-time',
      experience: 'Mid Level (3-5 years)',
      experienceYears: '3+ years',
      description: 'Analyze complex datasets and build predictive models for business insights.',
      requirements: ['Python, R, SQL', 'Machine learning', 'Statistical analysis'],
      posted: '5 days ago',
      salary: '£60,000 - £85,000'
    },
    {
      id: 5,
      title: 'DevOps Engineer',
      department: 'Engineering',
      location: 'New York, USA',
      country: 'USA',
      city: 'New York',
      type: 'Remote',
      experience: 'Mid Level (3-5 years)',
      experienceYears: '4+ years',
      description: 'Build and maintain CI/CD pipelines and cloud infrastructure.',
      requirements: ['Kubernetes, Docker', 'AWS/GCP', 'Infrastructure as Code'],
      posted: '1 week ago',
      salary: '$85,000 - $115,000'
    },
    {
      id: 6,
      title: 'Business Development Manager',
      department: 'Sales',
      location: 'Mumbai, India',
      country: 'India',
      city: 'Mumbai',
      type: 'Full-time',
      experience: 'Senior Level (5+ years)',
      experienceYears: '6+ years',
      description: 'Drive business growth and establish strategic partnerships.',
      requirements: ['B2B sales experience', 'Technology sector knowledge', 'Relationship building'],
      posted: '4 days ago',
      salary: '₹15,00,000 - ₹25,00,000'
    },
    {
      id: 7,
      title: 'Full Stack Developer',
      department: 'Engineering',
      location: 'Toronto, Canada',
      country: 'Canada',
      city: 'Toronto',
      type: 'Hybrid',
      experience: 'Mid Level (3-5 years)',
      experienceYears: '3+ years',
      description: 'Develop modern web applications using React, Node.js, and cloud technologies.',
      requirements: ['React, Node.js', 'Database design', 'API development'],
      posted: '6 days ago',
      salary: 'CAD $70,000 - $95,000'
    },
    {
      id: 8,
      title: 'Digital Transformation Consultant',
      department: 'Consulting',
      location: 'Sydney, Australia',
      country: 'Australia',
      city: 'Sydney',
      type: 'Full-time',
      experience: 'Senior Level (5+ years)',
      experienceYears: '5+ years',
      description: 'Guide organizations through digital transformation journeys.',
      requirements: ['Change management', 'Process optimization', 'Technology strategy'],
      posted: '1 week ago',
      salary: 'AUD $95,000 - $130,000'
    },
    {
      id: 9,
      title: 'HR Business Partner',
      department: 'HR',
      location: 'Berlin, Germany',
      country: 'Germany',
      city: 'Berlin',
      type: 'Full-time',
      experience: 'Mid Level (3-5 years)',
      experienceYears: '4+ years',
      description: 'Partner with business leaders to drive HR strategy and employee engagement.',
      requirements: ['HR generalist experience', 'Change management', 'Employee relations'],
      posted: '3 days ago',
      salary: '€55,000 - €75,000'
    },
    {
      id: 10,
      title: 'VP of Engineering',
      department: 'Management',
      location: 'Tokyo, Japan',
      country: 'Japan',
      city: 'Tokyo',
      type: 'Full-time',
      experience: 'Executive Level (10+ years)',
      experienceYears: '12+ years',
      description: 'Lead engineering teams and drive technical strategy across the organization.',
      requirements: ['Engineering leadership', 'Strategic planning', 'Team management'],
      posted: '1 week ago',
      salary: '¥12,000,000 - ¥18,000,000'
    }
  ];

  const filteredJobs = jobs.filter(job => {
    const matchesDepartment = filters.department === 'All' || job.department === filters.department;
    const matchesLocation = filters.location === 'All' || job.country === filters.location;
    const matchesExperience = filters.experience === 'All' || job.experience === filters.experience;
    const matchesType = filters.type === 'All' || job.type === filters.type;
    const matchesSearch = filters.search === '' || 
      job.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      job.description.toLowerCase().includes(filters.search.toLowerCase()) ||
      job.location.toLowerCase().includes(filters.search.toLowerCase());
    
    return matchesDepartment && matchesLocation && matchesExperience && matchesType && matchesSearch;
  });

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      department: 'All',
      location: 'All',
      experience: 'All',
      type: 'All',
      search: ''
    });
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, startIndex + jobsPerPage);

  return (
    <>
      <Helmet>
        <title>Careers at AuVSys - Join Our Global Technology Team</title>
        <meta name="description" content="Explore career opportunities at AuVSys. Join our global team of technology experts across 50+ countries." />
      </Helmet>
      
      <div className="careers-page">
        <div className="container">
          <motion.div 
            className="page-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Join Our Global Team</h1>
            <p>Shape the future of technology with AuVSys. Discover opportunities across 50+ countries.</p>
          </motion.div>

          <div className="careers-stats">
            <div className="stat-item">
              <h3>500+</h3>
              <p>Global Clients</p>
            </div>
            <div className="stat-item">
              <h3>50+</h3>
              <p>Countries</p>
            </div>
            <div className="stat-item">
              <h3>25+</h3>
              <p>Years Experience</p>
            </div>
            <div className="stat-item">
              <h3>10,000+</h3>
              <p>Team Members</p>
            </div>
          </div>

          <div className="job-search-section">
            <div className="search-bar">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search jobs by title, location, or keywords..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
            
            <div className="filter-controls">
              <button 
                className="filter-toggle"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter size={20} />
                Filters ({Object.values(filters).filter(f => f !== 'All' && f !== '').length})
              </button>
              
              {Object.values(filters).some(f => f !== 'All' && f !== '') && (
                <button className="clear-filters" onClick={clearFilters}>
                  <X size={16} />
                  Clear All
                </button>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="advanced-filters">
              <div className="filter-group">
                <label>Department</label>
                <select 
                  value={filters.department} 
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                >
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Country</label>
                <select 
                  value={filters.location} 
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                >
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Experience Level</label>
                <select 
                  value={filters.experience} 
                  onChange={(e) => handleFilterChange('experience', e.target.value)}
                >
                  {experienceLevels.map(exp => (
                    <option key={exp} value={exp}>{exp}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Job Type</label>
                <select 
                  value={filters.type} 
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  {jobTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="results-summary">
            <p>{filteredJobs.length} jobs found</p>
          </div>

          <div className="jobs-grid">
            {currentJobs.map((job, index) => (
              <motion.div
                key={job.id}
                className="job-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="job-header">
                  <h3>{job.title}</h3>
                  <span className="job-department">{job.department}</span>
                </div>
                
                <div className="job-meta">
                  <div className="meta-item">
                    <MapPin size={16} />
                    <span>{job.location}</span>
                  </div>
                  <div className="meta-item">
                    <Clock size={16} />
                    <span>{job.type}</span>
                  </div>
                  <div className="meta-item">
                    <Users size={16} />
                    <span>{job.experienceYears}</span>
                  </div>
                </div>
                
                <div className="job-salary">
                  <strong>{job.salary}</strong>
                </div>

                <p className="job-description">{job.description}</p>

                <div className="job-requirements">
                  <h4>Key Requirements:</h4>
                  <ul>
                    {job.requirements.map((req, idx) => (
                      <li key={idx}>{req}</li>
                    ))}
                  </ul>
                </div>

                <div className="job-footer">
                  <span className="posted-date">Posted {job.posted}</span>
                  <Link to={`/careers/job/${job.id}`} className="apply-btn">
                    View Details <ChevronRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  className={`page-btn ${currentPage === index + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
              
              <button 
                className="page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CareersPage;